/**
 * Mock SDK Adapter — for testing and demo purposes
 */

import type { SdkAdapter } from "../lib/task-runner.js";
import type { MemoryRef } from "../lib/memory-store.js";

export class MockAdapter implements SdkAdapter {
  private responses: Record<string, (userRequest: string) => string>;
  private defaultLatencyMs: number;
  private lastMemoryRef: MemoryRef | null = null;

  constructor(options?: {
    responses?: Record<string, (userRequest: string) => string>;
    defaultLatencyMs?: number;
  }) {
    this.responses = options?.responses ?? {};
    this.defaultLatencyMs = options?.defaultLatencyMs ?? 0;
  }

  async send(prompt: string, _options: import("../lib/task-runner.js").AdapterSendOptions): Promise<string> {
    const match = prompt.match(/## Task: (\S+)/);
    if (!match) {
      throw new Error(`MockAdapter: no "## Task: <id>" marker found in prompt`);
    }
    const taskId = match[1];

    const reqMatch = prompt.match(/## User Request\n\n([\s\S]*?)(?:\n\n---|$)/);
    const userRequest = reqMatch?.[1]?.trim() ?? "";

    if (this.defaultLatencyMs > 0) {
      await new Promise((r) => setTimeout(r, this.defaultLatencyMs));
    }

    const responseFn = this.responses[taskId];
    if (!responseFn) {
      return `Task "${taskId}" completed but no structured output available.`;
    }

    const result = responseFn(userRequest);
    this.lastMemoryRef = {
      id: `mock-ref-${Date.now()}`,
      provider: "mock",
      compat: "mock@1",
      created_at: new Date().toISOString(),
    };
    return result;
  }

  getLastMemoryRef(): MemoryRef | null {
    return this.lastMemoryRef;
  }

  isCompatible(compat: string): boolean {
    return compat.startsWith("mock@");
  }
}
